import { useState } from "react";
import { useGetList } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, Plus, Star, Briefcase } from "lucide-react";
import { SubStatusBadge } from "./SubStatusBadge";
import { SubDetail } from "./SubDetail";
import { CreateSubDialog } from "./CreateSubDialog";

interface Sub {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string;
  specialties: string[];
  status: string;
  avg_rating: number;
  total_jobs: number;
  completed_jobs: number;
  on_time_pct: number;
}

/**
 * Full-page subcontractor list.
 * Registered as a CustomRoute at /subcontractors.
 */
export function SubList() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isPending, total } = useGetList<Sub>("subcontractors", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "last_name", order: "ASC" },
  });

  if (selectedId) {
    return <SubDetail subId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <HardHat className="w-5 h-5" />
          Subcontractors
          {total !== undefined && total > 0 && (
            <span className="text-sm font-normal text-muted-foreground">({total})</span>
          )}
        </h2>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Sub
        </button>
      </div>

      {isPending && (
        <p className="text-sm text-muted-foreground py-4">Loading…</p>
      )}

      {!isPending && (!data || data.length === 0) && (
        <Card>
          <CardContent className="p-8 text-center">
            <HardHat className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground mb-3">No subcontractors yet.</p>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 mx-auto px-3 py-1.5 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-3 h-3" /> Add First Subcontractor
            </button>
          </CardContent>
        </Card>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((sub) => {
            const name = `${sub.first_name} ${sub.last_name}`.trim() || sub.company_name;
            return (
              <Card
                key={sub.id}
                className="cursor-pointer hover:border-blue-200 transition-colors"
                onClick={() => setSelectedId(sub.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <SubStatusBadge status={sub.status} />
                        {sub.specialties.slice(0, 3).map((s: string) => (
                          <span key={s} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                      <p className="text-sm font-medium truncate">{name}</p>
                      {sub.company_name && sub.first_name && (
                        <p className="text-xs text-muted-foreground">{sub.company_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
                      {Number(sub.avg_rating) > 0 && (
                        <span className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-amber-500" />
                          {Number(sub.avg_rating).toFixed(1)}
                        </span>
                      )}
                      <span className="flex items-center gap-0.5">
                        <Briefcase className="w-3 h-3" />
                        {sub.completed_jobs}/{sub.total_jobs}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateSubDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => setSelectedId(id)}
      />
    </div>
  );
}

SubList.path = "/subcontractors";
